import React, { ReactNode, createContext, useContext, useEffect, useMemo } from 'react';
import { format } from 'date-fns';

import { ForecastsView } from '@futrli/views/views/forecasts/ForecastsView/ForecastsView';
import { matchOrgDetailPath } from '@futrli/shared-services';
import { useApiView } from '@futrli/views/hooks/useApiView';

import { convertDDMMYYYtoYYYYMMDD } from '../../../utils/dates';
import { ForecastKindOption, ForecastTypeOption } from '../types';
import { getForecastWindowPayload } from '../get-create-forecast-request-data';
import { NewForecastDataSource } from '../../dashboard/types';
import { useCreateForecastForm } from '../hooks/useCreateForecastForm';
import { useExcelForecastUploadField } from '../hooks/useExcelForecastUploadField';
import { useGenerateExcelForecastTemplate } from '../hooks/useGenerateExcelForecastTemplate';

interface CreateForecastWizardContextProps {
  children?: ReactNode;
  kind: ForecastKindOption;
}

interface CreateForecastWizardContextValues {
  excelField: ReturnType<typeof useExcelForecastUploadField>;
  excelTemplate: ReturnType<typeof useGenerateExcelForecastTemplate>;
  form: ReturnType<typeof useCreateForecastForm>;
  isExcelUpload: boolean;
  kind: ForecastKindOption;
  view: ForecastsView;
}

// Normalize inactive fields so they cannot invalidate the memoized template payload.
const getTemplateWindowInputs = (
  kind: ForecastKindOption,
  form: ReturnType<typeof useCreateForecastForm>,
  from?: string
) => {
  const type = kind === 'scenario' ? form.type : undefined;
  return {
    durationFixed: type === ForecastTypeOption.Fixed ? form.durationFixed : '',
    durationRolling: type === ForecastTypeOption.Rolling ? form.durationRolling : '',
    previousActuals: type === ForecastTypeOption.Rolling && form.previousActuals,
    type,
    // Rolling windows derive their dates in the shared builder, not from the form's last-sync date.
    windowFrom: type === ForecastTypeOption.Rolling ? '' : from
  };
};

const defaultState = ({} as unknown) as CreateForecastWizardContextValues;
export const CreateForecastWizardContext = createContext(defaultState);

export const CreateForecastWizardContextProvider = ({ children, kind }: CreateForecastWizardContextProps) => {
  const match = matchOrgDetailPath(window.location.pathname, false);
  const workspaceId = match?.params?.workspaceId || '';
  const organisationId = match?.params?.organisationId || '';

  const view = useApiView(useMemo(() => new ForecastsView(workspaceId, organisationId), [workspaceId, organisationId]));
  const { createForecast } = view;
  const { datasourceModifiedDate, organisationCreated } = view.state;

  const form = useCreateForecastForm(kind, {}, organisationCreated);
  const fromYYYYMMDD = convertDDMMYYYtoYYYYMMDD(form.from.trim());
  const isExcelUpload = form.datasource === NewForecastDataSource.FromExcelUpload;

  const excelField = useExcelForecastUploadField();
  // Keep copy-only validation and the uploaded file out of template configuration validity.
  const isTemplateReady = form.isTemplateConfigurationValid && !!fromYYYYMMDD;
  const { durationFixed, durationRolling, previousActuals, type: templateType, windowFrom } =
    getTemplateWindowInputs(kind, form, fromYYYYMMDD);
  // Keep the payload reference stable for valid name edits and changes to inactive window fields.
  const templatePayload = useMemo(() => {
    if (!isTemplateReady || windowFrom === undefined) {
      return undefined;
    }

    return {
      forecast_kind: kind,
      ...getForecastWindowPayload({
        durationFixed,
        durationRolling,
        kind,
        previousActuals,
        type: templateType,
        windowFrom
      })
    };
  }, [durationFixed, durationRolling, isTemplateReady, kind, previousActuals, templateType, windowFrom]);
  // Switching datasource hides the template without invalidating an unchanged configuration.
  const excelTemplate = useGenerateExcelForecastTemplate(workspaceId, organisationId, templatePayload, isExcelUpload);

  const { setFrom, type } = form;

  // Set the 'from' date to the last modified date of the datasource if forecast type is 'rolling'
  useEffect(() => {
    if (type === 'rolling' && datasourceModifiedDate) {
      setFrom(format(Date.parse(datasourceModifiedDate), 'dd/MM/yyyy'));
    } else {
      setFrom('');
    }
  }, [datasourceModifiedDate, type, setFrom]);

  const values = {
    createForecast,
    excelField,
    excelTemplate,
    form,
    isExcelUpload,
    kind,
    type,
    view
  };

  return <CreateForecastWizardContext.Provider value={values}>{children}</CreateForecastWizardContext.Provider>;
};

export const useCreateForecastWizardContext = () => useContext(CreateForecastWizardContext);
